import { useState, type FormEvent } from "react";
import type { OidcApplication } from "../shared/types";
import { splitList, usePatchDraft } from "./admin-model";
import {
  ApplicationConnectionFields,
  ApplicationGeneralFields,
  ApplicationPolicyFields,
} from "./ApplicationFormSections";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import type { ApplicationDraft } from "./types";

function initialDraft(application?: OidcApplication): ApplicationDraft {
  return application
    ? {
        clientId: application.clientId,
        displayName: application.displayName,
        description: application.description,
        owner: application.owner,
        environment: application.environment,
        status: application.status,
        redirectUris: application.redirectUris,
        tokenEndpointAuthMethod: application.tokenEndpointAuthMethod,
        allowedScopes: application.allowedScopes,
        loginMode: application.loginMode,
        fixedUsername: application.fixedUsername,
        accessTokenTtlSeconds: application.accessTokenTtlSeconds,
        authorizationCodeTtlSeconds: application.authorizationCodeTtlSeconds,
        authTransactionTtlSeconds: application.authTransactionTtlSeconds,
      }
    : {
        clientId: "",
        displayName: "",
        description: "",
        owner: "CODEMONDAY",
        environment: "production",
        status: "active",
        redirectUris: [
          "https://codemonday.cloudflareaccess.com/cdn-cgi/access/callback",
        ],
        tokenEndpointAuthMethod: "client_secret_basic",
        allowedScopes: ["openid", "email", "profile", "groups"],
        loginMode: "standard",
        accessTokenTtlSeconds: 300,
        authorizationCodeTtlSeconds: 60,
        authTransactionTtlSeconds: 600,
      };
}

interface Props {
  application?: OidcApplication;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (draft: ApplicationDraft) => void | Promise<void>;
}

export function ApplicationDialog({
  application,
  busy,
  error,
  onClose,
  onSave,
}: Props) {
  const [draft, patchDraft] = usePatchDraft(() => initialDraft(application));
  const [redirectText, setRedirectText] = useState(
    draft.redirectUris.join("\n"),
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    void onSave({
      ...draft,
      redirectUris: splitList(redirectText, "\n"),
      ...(draft.loginMode === "fixed"
        ? { fixedUsername: draft.fixedUsername?.trim().toLowerCase() }
        : { fixedUsername: undefined }),
    });
  }

  return (
    <Modal
      closeDisabled={busy}
      eyebrow="OIDC application"
      onClose={onClose}
      title={application ? "Edit application" : "Register application"}
      titleId="application-dialog-title"
      wide
    >
      <form className="dialog__form" onSubmit={submit}>
        <div className="dialog__body">
          <ApplicationGeneralFields
            application={application}
            draft={draft}
            onPatch={patchDraft}
          />
          <ApplicationConnectionFields
            draft={draft}
            onPatch={patchDraft}
            onRedirectTextChange={setRedirectText}
            redirectText={redirectText}
          />
          <ApplicationPolicyFields draft={draft} onPatch={patchDraft} />

          <div className="invariant-strip">
            <Icon name="security" size={17} />
            <span>
              <strong>Provider enforced</strong>
              Authorization Code · S256 PKCE · exact redirects · RS256 · query
              response mode
            </span>
          </div>
          {error && (
            <div className="notice notice--error" role="alert">
              {error}
            </div>
          )}
        </div>
        <footer className="dialog__actions">
          <span className="dialog__action-note">
            All changes are audit logged.
          </span>
          <button
            className="button button--secondary"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="button button--primary"
            disabled={busy}
            type="submit"
          >
            {busy
              ? "Saving…"
              : application
                ? "Save changes"
                : "Register application"}
          </button>
        </footer>
      </form>
    </Modal>
  );
}
